import React, { useLayoutEffect } from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { ContactFooter } from '../components/sections/ContactFooter';

export const PrivacyPolicyPage = () => {
  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  const handleLogoClick = () => {
    window.location.href = '/';
  };

  return (
    <main className="min-h-screen">
      {/* Header - Made Sticky */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b shadow-lg" style={{borderColor: '#6B4423'}}>
        <div className="container mx-auto px-3 sm:px-4 md:px-6 py-2 sm:py-2 md:py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back button and A2ZCampus Logo */}
            <div className="flex items-center">
              <button
                onClick={handleBackToHome}
                className="mr-2 sm:mr-3 p-1 sm:p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Back to home"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" style={{color: '#6B4423'}} />
              </button>
              <button 
                onClick={handleLogoClick}
                className="flex items-center hover:opacity-80 transition-opacity cursor-pointer"
                aria-label="Go to home page"
              >
                <img 
                  src="/A2ZAssist%20logo.png" 
                  alt="A2ZCampus Logo" 
                  className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 mr-1 object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <div className="flex items-center">
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl tracking-tight" style={{color: '#2C2420'}}>A2Z</span>
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl tracking-tight" style={{color: '#6B4423'}}>Campus</span>
                  <span className="text-xs sm:text-sm md:text-sm lg:text-base font-bold ml-1 align-top" style={{color: '#6B4423'}}>™</span>
                </div>
              </button>
            </div>
            
            {/* Right: Privacy Policy Badge */}
            <div className="hidden lg:flex items-center bg-white border-2 rounded-2xl px-4 py-2 shadow-lg" style={{borderColor: '#C4A052'}}>
              <Shield className="w-5 h-5 mr-2" style={{color: '#C4A052'}} />
              <div className="flex items-center">
                <span className="font-bold text-base" style={{color: '#6B4423'}}>Privacy Policy</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-4 sm:py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="max-w-6xl mx-auto text-center">
            {/* Mobile Privacy Policy Badge */}
            <div className="lg:hidden flex justify-center mb-2 sm:mb-3 md:mb-4">
              <div className="flex items-center bg-white border-2 rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-2 shadow-lg" style={{borderColor: '#C4A052'}}>
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 mr-2 md:mr-3" style={{color: '#C4A052'}} />
                <div className="flex flex-col items-center">
                  <span className="font-bold text-xs sm:text-sm md:text-base text-center" style={{color: '#6B4423'}}>Privacy Policy</span>
                </div>
              </div>
            </div>

            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 md:mb-4 px-2" style={{color: '#2C2420'}}>
              Privacy <span style={{color: '#C4A052'}}>Policy</span>
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl max-w-4xl mx-auto leading-relaxed mb-2 sm:mb-2 md:mb-3 font-medium px-3" style={{color: '#6B4423'}}>
              Your privacy is important to us at A2ZCampus™
            </p>
            
            <p className="text-xs sm:text-sm md:text-base lg:text-lg max-w-4xl mx-auto leading-relaxed mb-3 sm:mb-4 md:mb-6 px-3" style={{color: '#2C2420'}}>
              This Privacy Policy explains how we collect, use, and protect your information when you use our ThinkTech™ platform and educational services.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-6 md:py-8" style={{backgroundColor: '#FFFFFF'}}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-gray-200">
              
              {/* Last Updated */}
              <div className="mb-6 md:mb-8 text-center">
                <p className="text-sm md:text-base" style={{color: '#6B4423'}}>
                  <strong>Last Updated:</strong> January 27, 2025
                </p>
              </div>

              <div className="space-y-6 md:space-y-8">
                
                {/* Introduction */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Introduction
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed mb-3" style={{color: '#2C2420'}}>
                    Welcome to A2ZCampus™, India's First ThinkTech Platform. We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy describes how A2Z Assist ("we," "our," or "us") collects, uses, and protects information when you use our website, workshops, and educational services.
                  </p>
                </div>

                {/* Information We Collect */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Information We Collect
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold mb-2" style={{color: '#6B4423'}}>Personal Information</h3>
                      <ul className="list-disc list-inside text-sm md:text-base leading-relaxed space-y-1" style={{color: '#2C2420'}}>
                        <li>Name, email address, and contact information</li>
                        <li>Educational institution and course details</li>
                        <li>Workshop registration and feedback information</li>
                        <li>Communication preferences</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2" style={{color: '#6B4423'}}>Usage Information</h3>
                      <ul className="list-disc list-inside text-sm md:text-base leading-relaxed space-y-1" style={{color: '#2C2420'}}>
                        <li>Website usage patterns and analytics</li>
                        <li>Workshop attendance and participation data</li>
                        <li>Feedback and survey responses</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* How We Use Information */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    How We Use Your Information
                  </h2>
                  <ul className="list-disc list-inside text-sm md:text-base leading-relaxed space-y-2" style={{color: '#2C2420'}}>
                    <li>To provide and improve our educational services and workshops</li>
                    <li>To communicate with you about programs, events, and opportunities</li>
                    <li>To customize content and experiences based on your interests</li>
                    <li>To analyze and improve our ThinkTech™ platform performance</li>
                    <li>To comply with legal obligations and protect our rights</li>
                  </ul>
                </div>

                {/* Information Sharing */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Information Sharing
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed mb-3" style={{color: '#2C2420'}}>
                    We do not sell, trade, or rent your personal information to third parties. We may share information only in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside text-sm md:text-base leading-relaxed space-y-2" style={{color: '#2C2420'}}>
                    <li>With partner educational institutions for workshop coordination</li>
                    <li>With service providers who assist in delivering our services</li>
                    <li>When required by law or to protect our legal rights</li>
                    <li>With your explicit consent</li>
                  </ul>
                </div>

                {/* Data Security */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Data Security
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed" style={{color: '#2C2420'}}>
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </div>

                {/* Your Rights */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Your Rights
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed mb-3" style={{color: '#2C2420'}}>
                    You have the right to:
                  </p>
                  <ul className="list-disc list-inside text-sm md:text-base leading-relaxed space-y-2" style={{color: '#2C2420'}}>
                    <li>Access and review your personal information</li>
                    <li>Request corrections to inaccurate information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Opt-out of marketing communications</li>
                    <li>Withdraw consent where applicable</li>
                  </ul>
                </div>

                {/* Cookies and Tracking */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Cookies and Tracking
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed" style={{color: '#2C2420'}}>
                    We use cookies and similar technologies to enhance your experience on our website, analyze usage patterns, and provide personalized content. You can control cookie settings through your browser preferences.
                  </p>
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Contact Us
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed mb-3" style={{color: '#2C2420'}}>
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-4 md:p-6">
                    <div className="space-y-2">
                      <p className="text-sm md:text-base" style={{color: '#2C2420'}}>
                        <strong>Email:</strong> <a href="mailto:info@a2zassist.com" className="hover:underline" style={{color: '#7A9CB5'}}>info@a2zassist.com</a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Changes to Policy */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
                    Changes to This Policy
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed" style={{color: '#2C2420'}}>
                    We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services after any changes constitutes acceptance of the updated policy.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactFooter />
    </main>
  );
};